import { useState, useCallback } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { 
  FileText, Send, Download, Loader2, Sparkles, Clock, RefreshCw
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Genera un informe ejecutivo general de la empresa con resumen de empleados, nómina y asistencia",
  "Informe de rotación de personal con análisis por departamento",
  "Reporte de nómina del último período con desglose de impuestos",
  "Análisis de ausentismo y tendencias de asistencia del último mes",
  "Informe de capacitaciones completadas y pendientes por empleado",
  "Dashboard ejecutivo para presentar al consejo directivo",
];

const Reports = () => {
  const [prompt, setPrompt] = useState("");
  const [preview, setPreview] = useState("");
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const headers = useCallback(() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const handlePreview = async () => {
    if (!prompt.trim()) { toast.error("Escribe qué tipo de informe necesitas"); return; }
    setGenerating(true);
    setPreview("");
    try {
      const res = await axios.post(`${API}/reports/preview`, { prompt }, { headers: headers(), withCredentials: true });
      setPreview(res.data.content);
      toast.success("Vista previa generada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al generar vista previa");
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!prompt.trim()) { toast.error("Escribe qué tipo de informe necesitas"); return; }
    setDownloading(true);
    try {
      const res = await axios.post(`${API}/reports/generate`, { prompt }, {
        headers: headers(), withCredentials: true, responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `informe_${new Date().toISOString().slice(0, 10)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF descargado exitosamente");
    } catch (err) {
      toast.error("Error al generar PDF");
    } finally {
      setDownloading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/reports/history`, { headers: headers(), withCredentials: true });
      setHistory(res.data || []);
      setShowHistory(true);
    } catch {
      toast.error("Error al cargar historial");
    }
  };

  return (
    <Layout
      title="Informes con IA"
      subtitle="Genera informes ejecutivos de RH en PDF con inteligencia artificial"
      actions={
        <Button variant="outline" onClick={loadHistory} data-testid="report-history-btn">
          <Clock className="w-4 h-4 mr-2" /> Historial
        </Button>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Input Section */}
        <div className="lg:col-span-1 space-y-4">
          <Card data-testid="report-input-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-violet-500" />
                Solicitar Informe
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder="Describe el informe que necesitas...&#10;Ej: Genera un informe ejecutivo de nómina del último trimestre con desglose por departamento"
                rows={5}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                data-testid="report-prompt-input"
              />
              <div className="flex gap-2">
                <Button onClick={handlePreview} disabled={generating || !prompt.trim()} className="flex-1" data-testid="preview-report-btn">
                  {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  {generating ? "Generando..." : "Vista Previa"}
                </Button>
                <Button onClick={handleDownloadPDF} disabled={downloading || !prompt.trim()} variant="outline" data-testid="download-pdf-btn">
                  {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  PDF
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Suggestions */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-slate-500">Sugerencias de informes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setPrompt(s)}
                  className="w-full text-left text-sm p-2.5 rounded-lg bg-slate-50 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                  data-testid={`suggestion-${i}`}
                >
                  {s}
                </button>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Preview Section */}
        <div className="lg:col-span-2">
          {preview ? (
            <Card data-testid="report-preview-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="w-4 h-4 text-emerald-600" />
                    Vista Previa del Informe
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => { setPreview(""); setPrompt(""); }} data-testid="clear-preview-btn">
                      <RefreshCw className="w-3 h-3 mr-1" /> Limpiar
                    </Button>
                    <Button size="sm" onClick={handleDownloadPDF} disabled={downloading} data-testid="download-from-preview-btn">
                      {downloading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                      Descargar PDF
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-white border border-slate-200 rounded-lg p-6 max-h-[600px] overflow-y-auto prose prose-sm max-w-none" data-testid="report-content">
                  {preview.split("\n").map((line, i) => {
                    const trimmed = line.trim();
                    if (!trimmed) return <br key={i} />;
                    if (trimmed.startsWith("- ")) return <p key={i} className="ml-4 text-sm text-slate-700">{trimmed}</p>;
                    if (trimmed.length < 60 && trimmed === trimmed.toUpperCase()) return <h3 key={i} className="font-bold text-slate-900 mt-4 mb-2">{trimmed}</h3>;
                    return <p key={i} className="text-sm text-slate-700 mb-1">{trimmed}</p>;
                  })}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-12 text-center" data-testid="report-empty">
                <div className="w-16 h-16 rounded-2xl bg-violet-100 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-violet-500" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Agente de Informes con IA</h3>
                <p className="text-slate-500 max-w-md mx-auto">
                  Escribe una solicitud en lenguaje natural y el agente generará un informe ejecutivo profesional
                  basado en los datos reales de tu empresa. Puedes descargarlo en PDF.
                </p>
              </CardContent>
            </Card>
          )}

          {/* History */}
          {showHistory && history.length > 0 && (
            <Card className="mt-6" data-testid="report-history-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Historial de Informes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {history.map((r, i) => (
                  <div key={r.report_id || i} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
                    <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{r.prompt}</p>
                      <p className="text-xs text-slate-500">{r.created_at?.slice(0, 16).replace("T", " ")}</p>
                    </div>
                    <Badge variant="outline" className="text-xs">PDF</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Reports;
